import { arrayMove } from "@dnd-kit/sortable";
import { hotkeyIdentity, isReservedHotkey } from "@goalhorns/bundle";
import type { Slot } from "../../lib/api-types";

/** One slot as the editor holds it before saving. Array order is playback order. */
export interface DraftSlot {
  readonly personId: string;
  readonly active: boolean;
  readonly hotkey: string | null;
}

export type ConflictKind = "duplicate" | "reserved";

/** Server slots in position order, minus people who are no longer on the live roster. */
export function draftFromServer(
  slots: readonly Slot[],
  livePersonIds: ReadonlySet<string>,
): DraftSlot[] {
  return [...slots]
    .sort((a, b) => a.position - b.position)
    .filter((slot) => livePersonIds.has(slot.personId))
    .map((slot) => ({ personId: slot.personId, active: slot.active, hotkey: slot.hotkey }));
}

export function addDraftSlot(list: readonly DraftSlot[], personId: string): DraftSlot[] {
  if (list.some((slot) => slot.personId === personId)) return [...list];
  return [...list, { personId, active: true, hotkey: null }];
}

export function removeDraftSlot(list: readonly DraftSlot[], personId: string): DraftSlot[] {
  return list.filter((slot) => slot.personId !== personId);
}

export function updateDraftSlot(
  list: readonly DraftSlot[],
  personId: string,
  change: Partial<Pick<DraftSlot, "active" | "hotkey">>,
): DraftSlot[] {
  return list.map((slot) => (slot.personId === personId ? { ...slot, ...change } : slot));
}

/** Moves the slot at `from` to `to` (both indices into `list`). Returns a new array. */
export function moveDraftSlot(list: readonly DraftSlot[], from: number, to: number): DraftSlot[] {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) {
    return [...list];
  }
  return arrayMove([...list], from, to);
}

/**
 * Client-side hotkey checks, the same rules the API and bundle apply. A duplicate means the
 * same key set as an earlier slot. Reserved keys (Escape, F11, Ctrl+S, ...) are refused.
 */
export function hotkeyConflicts(list: readonly DraftSlot[]): Map<string, ConflictKind> {
  const conflicts = new Map<string, ConflictKind>();
  const seen = new Map<string, string>();
  for (const slot of list) {
    if (slot.hotkey === null) continue;
    if (isReservedHotkey(slot.hotkey)) {
      conflicts.set(slot.personId, "reserved");
      continue;
    }
    const identity = hotkeyIdentity(slot.hotkey);
    const firstOwner = seen.get(identity);
    if (firstOwner === undefined) {
      seen.set(identity, slot.personId);
    } else {
      conflicts.set(slot.personId, "duplicate");
      conflicts.set(firstOwner, "duplicate");
    }
  }
  return conflicts;
}

export function toSlotsPayload(list: readonly DraftSlot[]): {
  slots: { personId: string; active: boolean; hotkey: string | null }[];
} {
  return {
    slots: list.map((slot) => ({
      personId: slot.personId,
      active: slot.active,
      hotkey: slot.hotkey,
    })),
  };
}

export function isSameDraft(a: readonly DraftSlot[], b: readonly DraftSlot[]): boolean {
  return (
    a.length === b.length &&
    a.every(
      (slot, index) =>
        slot.personId === b[index]?.personId &&
        slot.active === b[index]?.active &&
        slot.hotkey === b[index]?.hotkey,
    )
  );
}
